// React
import React, { useEffect, useState, useRef } from "react";

// Libraries
import PropTypes from "prop-types";
import { Label, Grid, Button, Icon } from "semantic-ui-react";
import { isEmpty, map, get, reduce, flattenDeep, filter } from "lodash";
import { useReactToPrint } from "react-to-print";

// Redux
import { connect } from "react-redux";
import { sendMessage } from "@Actions/chats";
import { displayNotification } from "@Redux/Actions/notifications";

// chat
import { messageBuilder, getTimeLabel } from "@Utils/functionality";
import { useListRef } from "@Features/Monitor/Context";

// Hooks
import useAlert from "@Hooks/useAlert";
import useWindowSize from "@Hooks/useWindowSize";
import usePanelVisibility from "@Hooks/usePanelVisibility";

// Utils
import { getExcludingNulls } from "@Utils";

// Components
import PrintRender from "@Components/PrintRender";
import CompanyFields from "@Components/CompanyFields";

import InfoBox from "@Components/InfoBox";
import ActionCard from "../ActionCard";
import ClockDisplays from "../ClockDisplays";

// Styles
import styles from "./styles.module.css";

const ChatInfo = ({
	isHistoric = false,
	currentChat,
	alerts,
	companyName,
	userName,
	dispatch,
	isChatDisabled = true,
	isWhatsappBlock = false,
	companyFields = [],
}) => {
	const { goList, goChat, isMobile, isDesktop } = usePanelVisibility();
	const print = useRef(null);
	const [printAction, setPrintAction] = useState(false);
	const [chunkSize, setChunkSize] = useState(3);
	const [channel, setChannel] = useState("");
	const [tags, setTags] = useState([]);
	const [group, setGroup] = useState({});
	const [showActions, setShowActions] = useState(false);
	const [contactInfo, setContactInfo] = useState({});
	const [currentContactInfo, setCurrentContactInfo] = useState({});
	const [showErrorsLabel, setShowErrorsLabel] = useState(false);
	const [languageOption, setLanguageOption] = useState("ES");
	const { width } = useWindowSize();
	const { listRef } = useListRef();
	const componentRef = useRef();
	const alert = useAlert(currentChat, alerts, isHistoric);

	useEffect(() => {
		if (width > 1600) {
			setChunkSize(3);
		} else {
			setChunkSize(2);
		}
	}, [width]);

	const prevChatIdRef = useRef();

	useEffect(() => {
		if (!isEmpty(currentChat)) {
			if (prevChatIdRef.current !== currentChat.id) {
				setShowActions(isHistoric);
				prevChatIdRef.current = currentChat.id;
			}
			setContactInfo(get(currentChat, "contact", {}));
			setChannel(get(currentChat, "channel.label", ""));
			setTags(get(currentChat, "tags", []));
			if (!isEmpty(get(currentChat, "group", ""))) {
				setGroup(get(currentChat, "group.name", ""));
			}
		}
	}, [currentChat, isHistoric]);

	const showActionsHandler = () => {
		setShowActions(!showActions);
		setShowErrorsLabel(false);
	};

	const platformName = get(currentChat, "channel.label", "");

	// tags handlers
	const renderChatTags = (array) => {
		if (isEmpty(array)) return null;
		const chunkedArray = reduce(
			array,
			(resultArray, item, index) => {
				const chunkIndex = Math.floor(index / chunkSize);
				if (!resultArray[chunkIndex]) {
					resultArray[chunkIndex] = [];
				}
				resultArray[chunkIndex].push(item);
				return resultArray;
			},
			[]
		);

		const rows = map(chunkedArray, (chunk, indexRow) => {
			const columns = map(chunk, (item, indexColumn) => {
				return (
					<Grid.Column className={styles.gridColumn} textAlign="center" key={indexColumn}>
						<Label className={styles.tag} style={{ backgroundColor: item.color, color: "white" }}>
							{item.name}
						</Label>
					</Grid.Column>
				);
			});
			return (
				<Grid.Row className={styles.gridRow} verticalAlign="middle" key={indexRow} columns={chunk.length}>
					{columns}
				</Grid.Row>
			);
		});
		return rows;
	};

	const renderChatActions = (array) => {
		if (isEmpty(array)) return null;
		const actions = map(array, (item) => {
			return (
				<div className={styles.printCardContainer} key={item.id}>
					<ActionCard chatAction={item} />
				</div>
			);
		});
		return actions;
	};

	const handleSendMessage = (text) => {
		const newMessage = messageBuilder({ chat: currentChat, reply: {}, userName, text });
		dispatch(sendMessage({ message: newMessage, listRef }));
	};

	const hasValue = (value) => {
		if (!isEmpty(value) && typeof value === "string") {
			const separatorSymbol = "§";
			const [firstValue] = value.split(separatorSymbol);

			return !isEmpty(firstValue);
		}

		if (typeof value === "boolean") {
			return value;
		}

		return !isEmpty(value);
	};

	const sendAllQuestions = () => {
		const fields = flattenDeep(
			map(currentContactInfo, (item) => {
				return get(item, "items", []);
			})
		);

		const fieldsWithQuestions = filter(fields, (item) => {
			return get(item, "question", false) && !hasValue(get(item, "value", ""));
		});

		const questions = map(fieldsWithQuestions, (item) => {
			return languageOption !== "ES" ? get(item, "question2", "") : get(item, "question", "");
		});

		const questionsText = questions
			.filter((str) => {
				return str !== "";
			})
			.join("\n \n");

		if (!isEmpty(questionsText)) {
			handleSendMessage(questionsText);
		} else {
			dispatch(
				displayNotification({
					text: "No hay preguntas creadas para enviar.",
					isError: true,
				})
			);
		}
	};

	const changeLanguage = () => {
		if (languageOption === "ES") {
			setLanguageOption("EN");
		} else {
			setLanguageOption("ES");
		}
	};

	const handlePrint = useReactToPrint({
		content: () => {
			return componentRef.current;
		},
	});

	if (isEmpty(currentChat)) return <div className={styles.container} />;
	return (
		<div className={styles.container}>
			{!isDesktop && (
				<div className={styles.backIcon} tabIndex={-1} role="button" onClick={isMobile ? goChat : goList}>
					<Icon link name="chevron left" />
				</div>
			)}
			<InfoBox title="Información de Chat" toggleable disabled={isChatDisabled}>
				<div className={styles.statusContainer}>
					<Grid className={styles.grid} centered>
						<Grid.Row className={styles.gridRow} verticalAlign="middle">
							<Grid.Column className={styles.gridColumn} width={isEmpty(currentChat.group) ? 12 : 6} textAlign="center">
								<Label className={styles.tag} color="grey">
									{channel}
								</Label>
							</Grid.Column>
							<Grid.Column className={styles.gridColumn} width={4} textAlign="center">
								<Label className={styles.tag} color={alert.color}>
									{alert.name}
								</Label>
							</Grid.Column>
							{!isEmpty(currentChat.group) && (
								<Grid.Column className={styles.gridColumn} width={6} textAlign="center">
									<Label className={styles.tag} color="blue">
										{get(currentChat, "group.name", "")}
									</Label>
								</Grid.Column>
							)}
						</Grid.Row>
					</Grid>
					<ClockDisplays
						messageTimestamp={getExcludingNulls(currentChat, "message.timestamp", 0)}
						createdDate={getExcludingNulls(currentChat, "createdDate", 0)}
						closeDate={get(currentChat, "closeDate", 0)}
						state={get(currentChat, "state", "Atender")}
						isHistoric={isHistoric}
					/>
					{!isEmpty(currentChat.tags) && (
						<Grid className={styles.grid} centered>
							{renderChatTags(get(currentChat, "tags", []))}
						</Grid>
					)}
				</div>
			</InfoBox>
			<div className={styles.showActionsButton}>
				<div>
					{showErrorsLabel && (
						<Label basic color="red" className={styles.errorLabel}>
							Completar campos requeridos
						</Label>
					)}
				</div>
				<div>
					{!isChatDisabled && !isWhatsappBlock && (
						<>
							{!isEmpty(companyFields) && (
								<Button toggle className={styles.sendButton} onClick={changeLanguage}>
									{languageOption}
								</Button>
							)}
							{!isEmpty(companyFields) && (
								<Button toggle className={styles.sendButton} onClick={sendAllQuestions}>
									<Icon name="send" />
								</Button>
							)}
						</>
					)}
					{!isHistoric && (
						<Button toggle className={!showActions ? styles.buttonInactive : styles.buttonActive} onClick={showActionsHandler}>
							<Icon name="lightning" />
						</Button>
					)}
					{isHistoric && (
						<div className={styles.actionsWrapper}>
							{!showActions && <Button className={styles.printButton} icon="print" onClick={handlePrint} />}

							<Button toggle className={!showActions ? styles.buttonInactive : styles.buttonActive} onClick={showActionsHandler}>
								<Icon name="lightning" />
							</Button>
						</div>
					)}
				</div>
			</div>
			<div className={`${styles.infoContainer} ${styles.printContainer}`} ref={componentRef}>
				<div className={styles.printOnly}>
					<h1 className={styles.companyTitle}>{companyName}</h1>
					<div className={styles.headerRightBlock}>
						<p className={styles.userName}>
							<span className={styles.headerTitlesss}>Usuario:</span>
							{userName}
						</p>
						<p className={styles.userName}>
							<span className={styles.headerTitlesss}>Fecha:</span>
							{getTimeLabel(new Date(), "DD/MM/YYYY, hh:mm A")}
						</p>
					</div>
				</div>

				{!showActions ? (
					<CompanyFields
						contactData={get(currentChat, "contact", {})}
						phoneEdit
						chatToRefresh={currentChat}
						setShowErrorsLabel={setShowErrorsLabel}
						showChannel={false}
						isHistoric={isHistoric}
						setCurrentContactInfo={setCurrentContactInfo}
						questionsLanguage={languageOption}
						noSendQuestion={isChatDisabled || isWhatsappBlock}
						handlePrint={handlePrint}
						disableFloatingButton={isChatDisabled}
						disabledStyle={isChatDisabled}
					/>
				) : (
					<InfoBox
						title="Acciones"
						buttonOnClick={() => {
							setPrintAction(!printAction);
						}}
						withButton
						buttonIcon="print">
						<div ref={print} className={styles.printGrid}>
							{renderChatActions(currentChat.chatActions)}
						</div>
					</InfoBox>
				)}
			</div>
			<PrintRender
				hiddenButon
				printAction={printAction}
				printActionRetry={() => {
					setPrintAction(false);
				}}
				printHeader={{
					company: companyName,
					user: userName,
					title: "Acciones",
				}}
				platform={platformName}
				clientName={contactInfo.name}
				clientPhone={contactInfo.phone}
				clientEmail={contactInfo.email}
				tags={tags}
				group={group}
				renderData={print}
				hideClientInfo={false}
			/>
		</div>
	);
};

ChatInfo.propTypes = {
	isHistoric: PropTypes.bool,
	isChatDisabled: PropTypes.bool,
	currentChat: PropTypes.object,
	alerts: PropTypes.array,
	companyName: PropTypes.string,
	userName: PropTypes.string,
	dispatch: PropTypes.func,
	isWhatsappBlock: PropTypes.bool,
	companyFields: PropTypes.array,
};

function mapStateToProps({ company, user }) {
	return {
		alerts: company.settings.alerts,
		companyName: company.name,
		userName: user.name,
		companyFields: company.companyFields,
	};
}

export default connect(mapStateToProps)(ChatInfo);
